import React from 'react';

import {PipelineStep} from 'pipelines/PipelineTypes';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import {GetActionInfoFn} from '../actions/createGetActionInfoFn';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

interface OptionInfo {
  title: string;
  icon: BenchlingIconName;
  onSelect?: () => void;
  isDisabled?: boolean;
  tooltipTitle?: string;
}

const SUPORTED_ACTIONS = [
  AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION,
  AnalysisFlowchartAction.DUPLICATE_STEPS,
  AnalysisFlowchartAction.DELETE_STEPS,
  AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN,
  AnalysisFlowchartAction.RERUN_STEP,
] as const;

/** @package Exported for tests */
export const ACTION_TITLE_AND_ICON = {
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: {title: 'Edit step', icon: 'edit'},
  [AnalysisFlowchartAction.DUPLICATE_STEPS]: {title: 'Duplicate step', icon: 'copy'},
  [AnalysisFlowchartAction.DELETE_STEPS]: {title: 'Delete step', icon: 'delete'},
  [AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN]: {title: 'Delete step and child steps', icon: 'delete'},
  [AnalysisFlowchartAction.RERUN_STEP]: {title: 'Rerun step', icon: 'rotate-right'},
} satisfies Record<(typeof SUPORTED_ACTIONS)[number], Pick<OptionInfo, 'title' | 'icon'>>;

function getOptionInfo(
  action: (typeof SUPORTED_ACTIONS)[number],
  stepId: string,
  getActionInfo: GetActionInfoFn
): OptionInfo | null {
  // Copying a step does not modify any existing step
  const stepIdsToModify = action === AnalysisFlowchartAction.DUPLICATE_STEPS ? [] : [stepId];
  const actionInfo = getActionInfo(action, ...stepIdsToModify);
  const {title, icon} = ACTION_TITLE_AND_ICON[action];

  if (!actionInfo.enabled) {
    if (actionInfo.message == null) {
      return null;
    }
    return {title, icon, isDisabled: true, tooltipTitle: actionInfo.message};
  }

  return {title, icon, onSelect: () => actionInfo.doAction(stepId)};
}

function getAvailableOptions(stepId: string, getActionInfo: GetActionInfoFn): Array<OptionInfo> {
  return SUPORTED_ACTIONS.map((action) => getOptionInfo(action, stepId, getActionInfo)).filter(
    (optionInfo) => optionInfo != null
  );
}

interface StepMenuProps {
  step: Pick<PipelineStep, 'id'>;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  children: JSX.Element;
}

/**
 * Shows a menu of options for a step.
 *
 * Currently, all menu options correspond to an action in `SUPORTED_ACTIONS` and are shown or hidden based on
 * `AnalysisFlowchartConfig.getActionInfo`.
 *
 * Assumes `AnalysisFlowchartContext` is available.
 */
export default function StepMenu({step, children, isMenuOpen, setIsMenuOpen}: StepMenuProps): JSX.Element {
  const {getActionInfo} = useAnalysisFlowchartContext();
  const availableOptions = getAvailableOptions(step.id, getActionInfo);

  if (availableOptions.length === 0) {
    return <>{children}</>;
  }

  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          {availableOptions.map((optionInfo) => (
            <Option key={optionInfo.title} {...optionInfo}>
              {optionInfo.title}
            </Option>
          ))}
        </Menu>
      }
      isOpen={isMenuOpen}
      onToggle={() => {
        if (isMenuOpen) {
          setIsMenuOpen(false);
        }
      }}
    >
      {children}
    </Dropdown>
  );
}
